import { useContext, useEffect, useRef, useState } from 'react'
import { logError, LogErrorMetrics } from '../../tracking/felog'
import { gnarClient, gnarSpec, setGnarUser } from '../../tracking/gnar'
import { sentrySetContext } from '../../tracking/sentry'
import { transformToObject } from '../../utils/error'
import { useCurrentUser } from '../../services/user'
import { useBrowserService, useExperimentService, useUserJourneyService, useLanguage } from '..'
import { PageContext } from '../../../contexts/page-context'
import { trackGA4PageView, trackContentInfo, trackExperimentInfo } from '../../tracking/google-tag-manager/events'
import { feMetrics } from '@lib/tracking/metrics'
import * as CmsMetrics from '@lib/tracking/metrics/metrics'
import { trackPerformance } from './track-performance'

export const usePageShow = (pageId: string, pageSlug: string, isPreview = false, isStaticFallback = false) => {
  const { user } = useCurrentUser()
  const { experimentsWithAssignedTreatment, isPageLogFeMetricsEnabled, isGDPR, isUSA, assignPagePathValidationAATest } =
    useExperimentService()
  const userJourney = useUserJourneyService()

  useEffect(() => {
    if (!isPreview) {
      // AA test. Does not change user experience.
      assignPagePathValidationAATest()

      void gnarSpec.pageShow(isStaticFallback, pageId, pageSlug, document.referrer, window.location.href)
      trackExperimentInfo(pageSlug, experimentsWithAssignedTreatment)
      trackGA4PageView(user)
      trackPerformance(pageId, pageSlug, document.referrer, window.location.href)

      if (!isGDPR && !isUSA) {
        userJourney.trackPageVisit()
      }

      if (isPageLogFeMetricsEnabled) {
        logFeMetricsPageShow(pageSlug)
      }
    }
  }, [
    pageId,
    pageSlug,
    isPreview,
    isStaticFallback,
    user,
    experimentsWithAssignedTreatment,
    isPageLogFeMetricsEnabled,
    isGDPR,
    isUSA,
    userJourney,
    assignPagePathValidationAATest,
  ])
}

const allowListFeMetricsPageShowPageSlug = new Set([
  '/',
  'a',
  'grammar-check',
  'plagiarism-checker',
  'paraphrasing-tool',
  'ai-detector',
])

const logFeMetricsPageShow = (pageSlug: string) => {
  if (allowListFeMetricsPageShowPageSlug.has(pageSlug)) {
    feMetrics
      .getMetric(CmsMetrics.GeneralClientMetrics.Page)
      .getCounter(CmsMetrics.ClientMetricsActions.Show)
      .setContext({
        pageSlug,
      })
      .increment()
  }
}

export const useGnarContainerId = () => {
  const [containerId, setContainerId] = useState('')
  const resolveRef = useRef<(value: string) => void>()
  const [containerIdPromise] = useState(() => {
    return new Promise<string>((resolve) => {
      resolveRef.current = resolve
    })
  })

  useEffect(() => {
    gnarClient
      .getContainerId()
      .then((id) => {
        setContainerId(id)
        resolveRef.current?.(id)
      })
      .catch((e) => {
        logError(`Error getting Gnar container id`, LogErrorMetrics.GnarContainerIdFailed, {
          error: transformToObject(e as Error),
        })
        // Return empty string as fallback
        resolveRef.current?.('')
      })
  }, [])

  return { containerId, containerIdPromise }
}

export const useTrackingInit = (pageId: string, pageSlug: string) => {
  const { user } = useCurrentUser()
  const { browserName } = useBrowserService()
  const { lang } = useLanguage()

  trackContentInfo(browserName, lang)

  useEffect(() => {
    // If the request fails, user will still be a None user. How should we handle tracking in that case?
    if (!user.isNone) {
      setGnarUser(user)
    }
  }, [user])
  useEffect(() => {
    sentrySetContext('page', { slug: pageSlug, id: pageId })
  }, [pageId, pageSlug])
}

export const useBannerShow = (isBannerVisible: boolean, bannerId: string, bannerCtaUrl?: string) => {
  const { pageId, slug } = useContext(PageContext)
  const bannerShowEventLogged = useRef(false)

  useEffect(() => {
    if (isBannerVisible && !bannerShowEventLogged.current) {
      gnarSpec.bannerShow(bannerId, pageId, slug, bannerCtaUrl)
      bannerShowEventLogged.current = true
    }
  }, [bannerId, bannerCtaUrl, isBannerVisible, pageId, slug])
}
